import { ReactLenis, useLenis } from 'lenis/react'
import 'lenis/dist/lenis.css'
import { useLayoutEffect, useSyncExternalStore, type ReactNode } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

function subscribe(onChange: () => void): () => void {
  const query = window.matchMedia(REDUCED_MOTION)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

const reducedMotion = () => window.matchMedia(REDUCED_MOTION).matches

/**
 * antd overlays that scroll on their own: Lenis leaves wheel and touch events
 * inside them to the browser. `allowNestedScroll` covers the rest (a wide
 * table's own scroll, a long Select list).
 */
const NATIVE_SCROLL =
  '.ant-modal-wrap, .ant-drawer, .ant-select-dropdown, .ant-dropdown, .ant-picker-dropdown, .ant-popover, .ant-image-preview-wrap'

/**
 * Lenis smooth scrolling for the page, as on the marketing website
 * (Website/fursahub/components/Global/SmoothScrolling.tsx), with the same
 * smoothing (lerp 0.05). Off for reduced motion, where the page scrolls
 * natively.
 */
export function SmoothScrolling({ children }: { children: ReactNode }) {
  const reduced = useSyncExternalStore(subscribe, reducedMotion, () => true)

  if (reduced) {
    return (
      <>
        <ScrollToTopOnNavigate />
        {children}
      </>
    )
  }

  return (
    <ReactLenis
      root
      options={{
        lerp: 0.05,
        allowNestedScroll: true,
        prevent: (node) => node.closest(NATIVE_SCROLL) !== null,
      }}
    >
      <ScrollToTopOnNavigate />
      {children}
    </ReactLenis>
  )
}

/**
 * A new page starts at the top. Back and forward keep the browser's position.
 * Before paint, so the new page never flashes at the old scroll position.
 */
function ScrollToTopOnNavigate() {
  const { pathname } = useLocation()
  const navigationType = useNavigationType()
  const lenis = useLenis()

  useLayoutEffect(() => {
    if (navigationType === 'POP') return
    if (lenis) lenis.scrollTo(0, { immediate: true, force: true })
    else window.scrollTo(0, 0)
    // Only a change of page; a new Lenis instance isn't a navigation.
  }, [pathname])

  return null
}
